import { useEffect, useRef } from 'react';

export const REANCHOR_WINDOW_MS = 2000;
const SCROLLER_POLL_MS = 200;
const SCROLLER_POLL_BUDGET_MS = 15000;

export interface ReanchorOnReflowOptions {
    active: boolean;
    findTarget: () => HTMLElement | null;
    onReanchor?: (target: HTMLElement) => void;
    windowMs?: number;
}

const isVerticallyScrollable = (element: HTMLElement): boolean => {
    const { overflowY } = window.getComputedStyle(element);
    return (overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight;
};

/**
 * The nearest ancestor that can actually move the element — an `overflow-y` container is only that
 * if it also overflows. A bounded table wrapper holding three rows scrolls nothing, so a correction
 * aimed at it is a no-op and the page scroller above it keeps the row where the reflow left it.
 */
export const findScrollingAncestor = (element: HTMLElement): HTMLElement | null => {
    for (let node = element.parentElement; node; node = node.parentElement) {
        if (isVerticallyScrollable(node)) return node;
    }
    return null;
};

const centerInScroller = (target: HTMLElement, scroller: HTMLElement) => {
    const targetBox = target.getBoundingClientRect();
    const scrollerBox = scroller.getBoundingClientRect();
    scroller.scrollTop += targetBox.top + targetBox.height / 2 - (scrollerBox.top + scrollerBox.height / 2);
};

/**
 * Keeps a just-scrolled-to element in view while the rest of the page is still arriving.
 *
 * A sibling section that loads on its own clock can mount ABOVE the element after the scroll has
 * already run, pushing it straight back out of view — the scroll was correct when it happened and
 * stale a moment later. While `active`, a reflow re-runs it.
 *
 * **What reflows is the scroller's content, not the document.** The page scroller here is a
 * fixed-height `overflow-y` div, so `document.body` never changes size when a late section lands
 * inside it and a body-only ResizeObserver sleeps through the entire event. The scroller's own
 * children are what grow, and they can only be found once there is a row to walk up from — hence
 * the short poll, which stops the moment it attaches.
 *
 * Two things keep this from becoming a permanent observer that fights the reader:
 *   - **The window starts at the first correction, not at mount.** What needs bounding is how long
 *     we chase a settling page, not how long we wait for it to start moving — the target often does
 *     not exist yet when the hook arms, and a window measured from mount would expire before the
 *     late section it exists to catch ever lands.
 *   - **Any sign of user intent cancels it outright.** Intent is read from wheel/touch/key/pointer
 *     rather than from a `scroll` listener, because our own correction fires scroll events: a
 *     scroll listener would cancel on the very re-anchor it is meant to protect.
 *
 * The correction is always instant. It is a correction and not a gesture, a smooth one would race
 * whatever smooth scroll is still in flight, and instant is also the right answer under reduced
 * motion — so there is no branch to get wrong. `onReanchor` runs after each one, for a caller that
 * needs to keep the row marked as long as we are still moving it.
 */
export function useReanchorOnReflow({ active, findTarget, onReanchor, windowMs = REANCHOR_WINDOW_MS }: ReanchorOnReflowOptions): void {
    const findTargetRef = useRef(findTarget);
    findTargetRef.current = findTarget;
    const onReanchorRef = useRef(onReanchor);
    onReanchorRef.current = onReanchor;

    useEffect(() => {
        if (!active || typeof ResizeObserver === 'undefined') return undefined;

        const intentEvents = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;
        let stopped = false;
        let measured = false;
        let waited = 0;
        let expiryId: ReturnType<typeof setTimeout> | undefined;
        let pollId: ReturnType<typeof setInterval> | undefined;

        const stop = () => {
            stopped = true;
            clearTimeout(expiryId);
            clearInterval(pollId);
            observer.disconnect();
            intentEvents.forEach((event) => window.removeEventListener(event, stop));
        };

        const reanchor = (target: HTMLElement) => {
            const scroller = findScrollingAncestor(target);
            if (scroller) centerInScroller(target, scroller);
            else target.scrollIntoView({ block: 'center' });
            onReanchorRef.current?.(target);
        };

        const observer = new ResizeObserver(() => {
            if (stopped) return;
            // The first callback of a batch reports the size the boxes already had on connect.
            if (!measured) {
                measured = true;
                return;
            }
            const target = findTargetRef.current();
            if (!target) return;
            if (!expiryId) expiryId = setTimeout(stop, windowMs);
            reanchor(target);
        });

        const observeScrollerContent = () => {
            const target = findTargetRef.current();
            const scroller = target && findScrollingAncestor(target);
            if (scroller) {
                measured = false;
                Array.from(scroller.children).forEach((child) => observer.observe(child));
                clearInterval(pollId);
                return;
            }
            waited += SCROLLER_POLL_MS;
            if (waited >= SCROLLER_POLL_BUDGET_MS) clearInterval(pollId);
        };

        observer.observe(document.body);
        pollId = setInterval(observeScrollerContent, SCROLLER_POLL_MS);
        intentEvents.forEach((event) => window.addEventListener(event, stop, { passive: true }));

        return stop;
    }, [active, windowMs]);
}
